import { BackendError, Service } from '@genial/services';
import type { V1GetAllTreasuriesWithdrawRequest } from '@genial/services/dist/apis';
import {
  TreasuryOfferBaseEntity,
  MetaResponse,
  ResponsesTreasuryMarketResponse,
  RiskResponseFilter,
  type ResponsesTreasuryWithdrawItemResponse,
} from '@genial/services/dist/models';
import { Utils } from '@genial/utils';
import { selector, selectorFamily } from 'recoil';

import { blankMetaResponse } from '../../../utils/blankMetaResponse';
import { transformSortItem } from '../../../utils/transformSortItem';
import { customerItemState } from '../../customer/atom';
import { advisorIdToApisSelector, partnerIdToApisSelector } from '../../master/selector';
import {
  productItemState,
  directTreasuryOffersBySearchTermParamsState,
  directTreasuryOffersParamsState,
  directTreasuryOffersBySearchTermState,
  directTreasuryOffersAllState,
  directTreasuryOffersByFilterParamsState,
  directTreasuryDetailSelectedState,
  directTreasuryMarketStatusState,
  directTreasuryRisksFilterState,
  directTreasuryIndexerFilterState,
  directTreasuryOffersOperationTypeState,
  directTreasuryRescueOffersParamsState,
  directTreasuryRescueOffersAllState,
  directTreasuryRescueOffersBySearchTermParamsState,
  directTreasuryRescueOffersBySearchTermState,
} from '../atom';
import { screenName } from '../constants';
import {
  normalizeDirectTreasuryFilters,
  normalizeDirectTreasuryFiltersFromMeta,
  normalizeDirectTreasuryOffers,
  normalizeDirectTreasurySellOffers,
} from '../normalizer';
import {
  DirectTreasuryIndexerCategoriesData,
  DirectTreasuryOfferEntityNormalized,
  DirectTreasuryOffersByFilterParams,
  DirectTreasuryOffersBySearchTerm,
  DirectTreasuryOffersParams,
  DirectTreasuryRisksCategoriesData,
  DirectTreasurySellOfferEntityNormalized,
  DynamicAssetsQueryCustomerParams,
  DynamicAssetsQueryParams,
  DynamicCustomFiltersProps,
  DynamicMarketStatusProps,
  type DirectTreasuryRescueOffersBySearchTerm,
} from '../types';

type DirectTreasuryOffersQueryData = {
  data: TreasuryOfferBaseEntity[];
  meta: MetaResponse;
};

export const directTreasuryOffersQuery = selectorFamily<
  DirectTreasuryOffersQueryData | null | undefined,
  { accountNumber: number; callEndpoint?: boolean }
>({
  key: 'NewApplication/directTreasuryOffersQuery',
  get: ({ accountNumber, callEndpoint }) => {
    return async ({ get }) => {
      if (!callEndpoint) {
        return;
      }
      try {
        const directTreasuryParams = get(directTreasuryOffersByFilterParamsState);
        const advisorSelected = get(advisorIdToApisSelector);
        const partnerSelected = get(partnerIdToApisSelector);

        const { data, meta } = await Service.Treasury.V1.getOffersFilter({
          ...directTreasuryParams,
          screenName,
          accountNumber,
          advisorId: advisorSelected,
          partnerId: partnerSelected,
        });

        const normalizedData = data?.map((item) => item.offer) || [];

        return { data: normalizedData, meta };
      } catch (error) {
        const backendError = error as BackendError;

        if (backendError.statusCode === 404) {
          return { data: [], meta: blankMetaResponse() };
        }

        throw error;
      }
    };
  },
});

export const directTreasuryOffersBySearchTermQuery = selectorFamily<
  DirectTreasuryOffersQueryData | null | undefined,
  { accountNumber: number }
>({
  key: 'NewApplication/directTreasuryOffersBySearchTermQuery',
  get: ({ accountNumber }) => {
    return async ({ get }) => {
      const directTreasuryParams = get(directTreasuryOffersBySearchTermParamsState);
      const advisorSelected = get(advisorIdToApisSelector);
      const partnerSelected = get(partnerIdToApisSelector);

      if (directTreasuryParams.skipEndpointCall) {
        return;
      }

      const { data, meta } = await Service.Treasury.V1.getOffersFilter({
        ...directTreasuryParams,
        screenName,
        accountNumber,
        advisorId: advisorSelected,
        partnerId: partnerSelected,
      });

      const normalizedData = data?.map((item) => item.offer) || [];

      return { data: normalizedData, meta };
    };
  },
});

export const directTreasuryOffersByFilterParams = selector({
  key: 'directTreasuryOffersByFilterParams',
  set: ({ set, get }, newValue) => {
    const currentDirectTreasuryOffersByFilter = get(directTreasuryOffersByFilterParamsState);
    const value = newValue as Partial<DirectTreasuryOffersByFilterParams>;

    const directTreasuryOffersByFilter = {
      ...currentDirectTreasuryOffersByFilter,
      ...value,
    };

    return set(directTreasuryOffersByFilterParamsState, directTreasuryOffersByFilter);
  },
  get: ({ get }) => {
    const item = get(directTreasuryOffersByFilterParamsState);
    return item;
  },
});

export const directTreasuryOffersParams = selector({
  key: 'directTreasuryOffersParams',
  set: ({ set, get }, newValue) => {
    const currentDirectTreasuryOffers = get(directTreasuryOffersParamsState);
    const value = newValue as Partial<DirectTreasuryOffersParams>;
    const directTreasuryOffers = { ...currentDirectTreasuryOffers, ...value };

    return set(directTreasuryOffersParamsState, directTreasuryOffers);
  },
  get: ({ get }) => {
    const item = get(directTreasuryOffersParamsState);
    return item;
  },
});

export const directTreasuryOffersBySearchTermParams = selector({
  key: 'directTreasuryOffersBySearchTermParams',
  set: ({ set, get }, newValue) => {
    const currentDirectTreasuryOffersBySearchTerm = get(
      directTreasuryOffersBySearchTermParamsState
    );
    const value = newValue as Partial<DirectTreasuryOffersBySearchTerm>;
    const directTreasuryOffersBySearchTerm = {
      ...currentDirectTreasuryOffersBySearchTerm,
      ...value,
    };

    return set(directTreasuryOffersBySearchTermParamsState, directTreasuryOffersBySearchTerm);
  },
  get: ({ get }) => {
    const item = get(directTreasuryOffersBySearchTermParamsState);
    return item;
  },
});

export const updateDirectTreasuryOffersAllSelector = selector({
  key: 'updateDirectTreasuryOffersAllSelector',
  set: ({ set }, newValue) => {
    const data = newValue as TreasuryOfferBaseEntity[];
    return set(directTreasuryOffersAllState, data);
  },
  get: ({ get }) => {
    const item = get(directTreasuryOffersAllState);
    return item;
  },
});

export const updateDirectTreasuryOffersBySearchTermSelector = selector({
  key: 'updateDirectTreasuryOffersBySearchTermSelector',
  set: ({ set }, newValue) => {
    const data = newValue as TreasuryOfferBaseEntity[];
    return set(directTreasuryOffersBySearchTermState, data);
  },
  get: ({ get }) => {
    const item = get(directTreasuryOffersBySearchTermState);
    return item;
  },
});

export const updateDirectTreasuryProductItemAmountSelector = selector({
  key: 'updateDirectTreasuryProductItemAmountSelector',
  set: ({ set, get }, newValue) => {
    const currentProductItem = get(productItemState);
    const productItemAmount = newValue as number;
    const data = {
      ...currentProductItem,
      current: { ...currentProductItem.current, amountProductItemSelected: productItemAmount },
    };
    return set(productItemState, data);
  },
  get: ({ get }) => {
    const item = get(productItemState);
    return item;
  },
});

export const updateDirectTreasuryProductDetailSelectedSelector = selector({
  key: 'updateDirectTreasuryProductDetailSelectedSelector',
  set: ({ set }, newValue) => {
    const productItemAmount = newValue as TreasuryOfferBaseEntity;
    return set(directTreasuryDetailSelectedState, productItemAmount);
  },
  get: ({ get }) => {
    const item = get(directTreasuryDetailSelectedState);
    return item;
  },
});

export const updateDirectTreasuryMarketStatusSelector = selector({
  key: 'updateDirectTreasuryMarketStatusSelector',
  set: ({ set }, newValue) => {
    const value = newValue as ResponsesTreasuryMarketResponse;
    return set(directTreasuryMarketStatusState, value);
  },
  get: ({ get }) => {
    const item = get(directTreasuryMarketStatusState);
    return item;
  },
});

export const updateDirectTreasuryProductItemPriceSelector = selector({
  key: 'updateDirectTreasuryProductItemPriceSelector',
  set: ({ set, get }, newValue) => {
    const currentProductItem = get(productItemState);
    const productItemPrice = newValue as number;
    const data = {
      ...currentProductItem,
      current: {
        ...currentProductItem.current,
        priceProductItemSelected: productItemPrice,
        appliedAmount: productItemPrice,
      },
    };
    return set(productItemState, data);
  },
  get: ({ get }) => {
    const item = get(productItemState);
    return item;
  },
});

export const directTreasuryIndexerQuery = selectorFamily<
  DirectTreasuryIndexerCategoriesData | null | undefined,
  { skipEndpointCall?: boolean }
>({
  key: 'NewApplication/directTreasuryIndexerCategoriesQuery',
  get: ({ skipEndpointCall }) => {
    return async ({ get }) => {
      try {
        if (skipEndpointCall) {
          const directTreasuryIndexerFilter = get(directTreasuryIndexerFilterState);
          return directTreasuryIndexerFilter;
        }
        const { data } = await Service.Treasury.V1.getOffersIndexerRisk({
          screenName,
        });

        const normalizedData = data.indexerRiskFilter.indexer?.map((item) => item) || [];

        return { data: normalizedData };
      } catch (error) {
        const backendError = error as BackendError;

        if (backendError.statusCode === 404) {
          return { data: [] };
        }

        throw error;
      }
    };
  },
});

export const updateDirectTreasuryIndexerOptionsFilterSelector = selector({
  key: 'updateDirectTreasuryIndexerOptionsFilterSelector',
  set: ({ set }, newValue) => {
    const value = { data: newValue } as { data: RiskResponseFilter[] };
    return set(directTreasuryIndexerFilterState, value);
  },
  get: ({ get }) => {
    const item = get(directTreasuryIndexerFilterState);
    return item;
  },
});

export const directTreasuryRisksQuery = selectorFamily<
  DirectTreasuryRisksCategoriesData | null | undefined,
  { skipEndpointCall?: boolean }
>({
  key: 'NewApplication/directTreasuryRisksCategoriesQuery',
  get: ({ skipEndpointCall }) => {
    return async ({ get }) => {
      try {
        if (skipEndpointCall) {
          const directTreasuryRisksFilter = get(directTreasuryRisksFilterState);
          return directTreasuryRisksFilter;
        }
        const { data } = await Service.Treasury.V1.getOffersIndexerRisk({
          screenName,
        });

        const normalizedData = data.indexerRiskFilter.risk?.map((item) => item) || [];

        return { data: normalizedData };
      } catch (error) {
        const backendError = error as BackendError;

        if (backendError.statusCode === 404) {
          return { data: [] };
        }

        throw error;
      }
    };
  },
});

export const updateDirectTreasuryRisksOptionsFilterSelector = selector({
  key: 'updateDirectTreasuryRisksOptionsFilterSelector',
  set: ({ set }, newValue) => {
    const value = { data: newValue } as {
      data: RiskResponseFilter[];
    };
    return set(directTreasuryRisksFilterState, value);
  },
  get: ({ get }) => {
    const item = get(directTreasuryRisksFilterState);
    return item;
  },
});

export const directTreasuryMarketStatusQuery = selector<DynamicMarketStatusProps>({
  key: 'NewApplication/directTreasuryMarketStatusQuery',
  get: async () => {
    const { data } = await Service.Treasury.V1.getMarketStatus({
      screenName,
    });

    return {
      isOpen: data?.marketStatus.isOpened,
      message: data?.marketStatus.message || '',
      originalData: data,
    };
  },
});

export const directTreasuryOffersQueryUpdated = selectorFamily<
  {
    data: DirectTreasuryOfferEntityNormalized[];
    meta: MetaResponse;
    filters?: DynamicCustomFiltersProps['filters'];
  },
  DynamicAssetsQueryParams
>({
  key: 'NewApplication/directTreasuryOffersQueryUpdated',
  get: ({ accountNumber, filtersToApply, searchTerm, page, orderBy }) => {
    return async ({ get }) => {
      try {
        const advisorSelected = get(advisorIdToApisSelector);
        const partnerSelected = get(partnerIdToApisSelector);
        const sortItems = transformSortItem(orderBy);

        const { data, meta } = await Service.Treasury.V1.getOffersFilter({
          ...filtersToApply,
          ...(searchTerm ? { searchTerm } : {}),
          sortItems: sortItems && [sortItems],
          page,
          limit: 12,
          screenName,
          accountNumber,
          advisorId: advisorSelected,
          partnerId: partnerSelected,
        });

        const normalizedData = normalizeDirectTreasuryOffers(data);
        const normalizedFilters = normalizeDirectTreasuryFiltersFromMeta(meta);

        return { data: normalizedData, meta, filters: normalizedFilters.filters };
      } catch (error) {
        const backendError = error as BackendError;

        if (backendError.statusCode === 404) {
          return { data: [], meta: blankMetaResponse() };
        }

        throw error;
      }
    };
  },
});

export const directTreasurySellOffersQueryUpdated = selectorFamily<
  { data: DirectTreasurySellOfferEntityNormalized[]; meta: MetaResponse },
  DynamicAssetsQueryParams
>({
  key: 'NewApplication/directTreasurySellOffersQueryUpdated',
  get: ({ accountNumber, document, searchTerm, page }) => {
    return async ({ get }) => {
      try {
        if (!document) {
          return { data: [], meta: blankMetaResponse() };
        }
        const advisorSelected = get(advisorIdToApisSelector);
        const partnerSelected = get(partnerIdToApisSelector);

        const serviceToCall = searchTerm
          ? Service.Treasury.V1.getWithdrawSearchTerm
          : Service.Treasury.V1.getAllWithdraw;

        const { data, meta } = await serviceToCall({
          page,
          cpfCnpj: Utils.Unmask.CpfOrCnpj(document),
          limit: 12,
          searchTerm: searchTerm || '',
          screenName,
          accountNumber,
          advisorId: advisorSelected,
          partnerId: partnerSelected,
        });

        const normalizedData = normalizeDirectTreasurySellOffers(data);

        return { data: normalizedData, meta };
      } catch (error) {
        const backendError = error as BackendError;

        if (backendError.statusCode === 404) {
          return { data: [], meta: blankMetaResponse() };
        }

        throw error;
      }
    };
  },
});

export const directTreasuryCustomFiltersQuery = selectorFamily<
  DynamicCustomFiltersProps,
  DynamicAssetsQueryCustomerParams
>({
  key: 'NewApplication/directTreasuryCustomFiltersQuery',
  get: () => {
    return async () => {
      const { data } = await Service.Treasury.V1.getOffersIndexerRisk({
        screenName,
      });

      const normalizedData = normalizeDirectTreasuryFilters(data);

      return normalizedData;
    };
  },
});

export const updateDirectTreasuryOffersOperationTypeSelector = selector({
  key: 'updateDirectTreasuryOffersOperationTypeSelector',
  get: ({ get }) => {
    const item = get(directTreasuryOffersOperationTypeState);
    return item;
  },
  set: ({ set }, newValue) => {
    const data = newValue;
    return set(directTreasuryOffersOperationTypeState, data);
  },
});

export const directTreasuryRescueOffersQuery = selector({
  key: 'NewApplication/directTreasuryRescueOffersQuery',
  get: async ({ get }) => {
    try {
      const directTreasuryRescueParams = get(directTreasuryRescueOffersParamsState);
      const advisorSelected = get(advisorIdToApisSelector);
      const partnerSelected = get(partnerIdToApisSelector);
      const { cpfCnpj, accountNumber } = get(customerItemState).current;

      if (!cpfCnpj) {
        return;
      }

      const { data, meta } = await Service.Treasury.V1.getAllWithdraw({
        ...directTreasuryRescueParams,
        screenName,
        accountNumber,
        cpfCnpj: Utils.Unmask.CpfOrCnpj(cpfCnpj),
        advisorId: advisorSelected,
        partnerId: partnerSelected,
      });

      const normalizedData = data?.map((item) => item.withdraw) || [];

      return { data: normalizedData, meta };
    } catch (error) {
      const backendError = error as BackendError;

      if (backendError.statusCode === 404) {
        return { data: [], meta: blankMetaResponse() };
      }

      throw error;
    }
  },
});

export const directTreasuryRescueOffersParams = selector({
  key: 'directTreasuryRescueOffersParams',
  set: ({ set, get }, newValue) => {
    const currentDirectTreasuryRescueOffers = get(directTreasuryRescueOffersParamsState);
    const value = newValue as Partial<V1GetAllTreasuriesWithdrawRequest>;
    const directTreasuryRescueOffers = { ...currentDirectTreasuryRescueOffers, ...value };

    return set(directTreasuryRescueOffersParamsState, directTreasuryRescueOffers);
  },
  get: ({ get }) => {
    const item = get(directTreasuryRescueOffersParamsState);
    return item;
  },
});

export const updateDirectTreasuryRescueOffersAllSelector = selector({
  key: 'updateDirectTreasuryRescueOffersAllSelector',
  set: ({ set }, newValue) => {
    const data = newValue as ResponsesTreasuryWithdrawItemResponse[];
    return set(directTreasuryRescueOffersAllState, data);
  },
  get: ({ get }) => {
    const item = get(directTreasuryRescueOffersAllState);
    return item;
  },
});

export const directTreasuryOffersRescueBySearchTermQuery = selector({
  key: 'NewApplication/directTreasuryOffersRescueBySearchTermQuery',
  get: async ({ get }) => {
    const directTreasuryRescueBySearchTermParams = get(
      directTreasuryRescueOffersBySearchTermParamsState
    );
    const advisorSelected = get(advisorIdToApisSelector);
    const partnerSelected = get(partnerIdToApisSelector);
    const { cpfCnpj, accountNumber } = get(customerItemState).current;

    if (directTreasuryRescueBySearchTermParams.skipEndpointCall || !cpfCnpj) {
      return;
    }

    const { data, meta } = await Service.Treasury.V1.getWithdrawSearchTerm({
      ...directTreasuryRescueBySearchTermParams,
      screenName,
      accountNumber,
      cpfCnpj: Utils.Unmask.CpfOrCnpj(cpfCnpj),
      advisorId: advisorSelected,
      partnerId: partnerSelected,
    });

    const normalizedData = data?.map((item) => item.withdraw) || [];

    return { data: normalizedData, meta };
  },
});

export const directTreasuryRescueOffersBySearchTermParams = selector({
  key: 'directTreasuryRescueOffersBySearchTermParams',
  set: ({ set, get }, newValue) => {
    const currentDirectTreasuryOffersRescueBySearchTerm = get(
      directTreasuryRescueOffersBySearchTermParamsState
    );
    const value = newValue as Partial<DirectTreasuryRescueOffersBySearchTerm>;
    const directTreasuryOffersBySearchTerm = {
      ...currentDirectTreasuryOffersRescueBySearchTerm,
      ...value,
    };

    return set(directTreasuryRescueOffersBySearchTermParamsState, directTreasuryOffersBySearchTerm);
  },
  get: ({ get }) => {
    const item = get(directTreasuryRescueOffersBySearchTermParamsState);
    return item;
  },
});

export const updateDirectTreasuryRescueOffersBySearchTermSelector = selector({
  key: 'updateDirectTreasuryRescueOffersBySearchTermSelector',
  set: ({ set }, newValue) => {
    const data = newValue as ResponsesTreasuryWithdrawItemResponse[];
    return set(directTreasuryRescueOffersBySearchTermState, data);
  },
  get: ({ get }) => {
    const item = get(directTreasuryRescueOffersBySearchTermState);
    return item;
  },
});
